import type { AppError, AsyncResult, Result } from '@shared/utils';
import { ok } from '@shared/utils';
import type { RuntimeKind } from '../types/RuntimeKind';
import type { EnvironmentConfig } from '../config/interfaces/EnvironmentConfig';
import type { PackageResourceConfig } from '../config/types/PackageResourceConfig';
import type { PackagePostBootstrapTask } from '../bootstrap/interfaces/PackagePostBootstrapTask';

/**
 * @group Bootstrap
 * @summary
 * <blockquote>Abstract base class that every shared package implements to participate
 * in the bootstrap lifecycle.</blockquote>
 *
 * @remarks
 * Subclasses define `configureRuntime` (called during bootstrap to wire up
 * adapters) and optionally override `getPostBootstrapTasks` to register
 * tasks that run after all packages have been configured.
 *
 * @example
 * ```typescript
 * import { PackageBootstrap } from '@shared/core';
 *
 * class LoggerPackageBootstrap extends PackageBootstrap<LoggerEnvironmentConfig> {
 *   readonly packageId = '@shared/logger';
 *   readonly runtimeKind = 'server';
 *
 *   async configureRuntime(config) {
 *     Logger.configureRuntime(config);
 *     return ok(undefined);
 *   }
 * }
 * ```
 */
export abstract class PackageBootstrap<TEnvConfig extends EnvironmentConfig<any>> {
  abstract readonly packageId: string;

  abstract readonly runtimeKind: RuntimeKind;

  abstract configureRuntime(
    config: PackageResourceConfig<TEnvConfig>
  ): AsyncResult<void, AppError>;

  getPostBootstrapTasks(): Result<ReadonlyArray<PackagePostBootstrapTask>, AppError> {
    return ok([]);
  }
}
